/** EightBots response forms follow the pinned Hermes server-request contracts. */
type Base = {id: string; method: string; sessionId: string; title: string};
export type NativeQuestionValue = {id: string; question: string; choices: string[]; multi: boolean};
export type NativeClarification = Base & {kind: 'clarify'; questions: NativeQuestionValue[]; locked: Record<string, string | null>};
export type NativeSetup = Base & {kind: 'setup'; question: string; options: {id: string; label: string; detail: string | null}[]; multi: boolean; preselected: string[]};
export type NativeApproval = Base & {kind: 'approval'; command: string; description: string; choices: ApprovalChoice[]};
export type NativeSecret = Base & {kind: 'secret'; prompt: string; detail: string | null; code: boolean};
export type NativeLogin = Base & {kind: 'login'; site: string; origin: string};
export type NativeUnsupported = Base & {kind: 'unsupported'; known: boolean; reason: string};
export type NativeRequest = NativeClarification | NativeSetup | NativeApproval | NativeSecret | NativeLogin | NativeUnsupported;
export type ApprovalChoice = 'once' | 'session' | 'always' | 'deny';
export const approvalLabels: Record<ApprovalChoice, string> = {
  once: 'Allow once', session: 'Allow for this chat', always: 'Always allow', deny: 'Do not allow',
};

function object(value: unknown, name: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error(`${name} must be an object.`);
  return value as Record<string, unknown>;
}
function text(value: unknown, name: string, empty = false): string {
  if (typeof value !== 'string' || (!empty && !value.trim())) throw new Error(`${name} must be text${empty ? '' : ' and cannot be empty'}.`);
  return value;
}
function optionalText(value: unknown, name: string): string | null {
  return value === undefined || value === null ? null : text(value, name, true);
}
function strings(value: unknown, name: string): string[] {
  if (value === undefined || value === null) return [];
  if (!Array.isArray(value) || !value.every(item => typeof item === 'string')) throw new Error(`${name} must be a list of text values.`);
  return value;
}
function flag(value: unknown, name: string): boolean {
  if (value === undefined) return false;
  if (typeof value !== 'boolean') throw new Error(`${name} must be true or false.`);
  return value;
}
function only(result: Record<string, unknown>, allowed: string[]): void {
  if (Object.keys(result).some(key => !allowed.includes(key))) throw new Error('The response contains fields that this request does not accept.');
}
const approvalChoices = new Set<ApprovalChoice>(['once', 'session', 'always', 'deny']);
const desktopMethods = new Set(['terminal.read', 'preview.read', 'window.read', 'preview.act', 'tour']);
const setupKinds = new Set(['question', 'accent', 'theme', 'layout', 'connectors', 'plugins', 'tour', 'fork', 'machine_use']);

export function parseNativeRequest(request: Record<string, unknown>): NativeRequest {
  const id = text(request.id, 'Request ID'), method = text(request.method, 'Request method');
  const params = object(request.params, 'Request parameters');
  const base = {id, method, sessionId: text(params.session_id, 'Request owner', true)};
  if (method === 'clarify') {
    if (!Array.isArray(params.questions) || !params.questions.length) throw new Error('Hermes supplied no questions.');
    const questions = params.questions.map(entry => {
      const question = object(entry, 'Question');
      return {id: text(question.qid, 'Question ID'), question: text(question.question, 'Question'),
        choices: strings(question.choices, 'Question choices'), multi: flag(question.multi_select, 'Multiple selection')};
    });
    if (new Set(questions.map(question => question.id)).size !== questions.length) throw new Error('Hermes supplied repeated question IDs.');
    const locked = params.answers === undefined || params.answers === null ? {} : object(params.answers, 'Saved answers');
    for (const [id, answer] of Object.entries(locked)) {
      if (!questions.some(question => question.id === id) || (answer !== null && typeof answer !== 'string')) {
        throw new Error('A saved answer does not match this question.');
      }
    }
    return {...base, kind: 'clarify', title: 'Answer the questions', questions, locked: locked as Record<string, string | null>};
  }
  if (method === 'setup_choose') {
    if (!setupKinds.has(text(params.kind, 'Setup choice kind'))) throw new Error('Hermes supplied an unknown setup choice kind.');
    const values = params.options ?? [];
    if (!Array.isArray(values)) throw new Error('Setup options must be a list.');
    const options = values.map(entry => {
      const option = object(entry, 'Setup option');
      return {id: text(option.id, 'Option ID'), label: text(option.label, 'Option label'), detail: optionalText(option.detail, 'Option detail')};
    });
    if (new Set(options.map(option => option.id)).size !== options.length) throw new Error('Hermes supplied repeated option IDs.');
    const preselected = strings(params.preselected, 'Selected options');
    if (preselected.some(id => !options.some(option => option.id === id))) throw new Error('A selected setup option is not in this request.');
    const multi = flag(params.multi_select, 'Multiple selection');
    if (!multi && preselected.length > 1) throw new Error('A single-choice request has multiple selected options.');
    return {...base, kind: 'setup', title: 'Choose an option', question: text(params.question, 'Setup question'), options, multi, preselected};
  }
  if (method === 'approval') {
    text(params.request_id, 'Approval ID');
    const choices = strings(params.choices, 'Approval choices');
    if (!choices.length || choices.some(choice => !approvalChoices.has(choice as ApprovalChoice))) throw new Error('Hermes supplied no supported approval choices.');
    if (choices.includes('always') && params.allow_permanent === false) throw new Error('This approval cannot allow permanent access.');
    if (choices.includes('session') && params.allow_session === false) throw new Error('This approval cannot allow access for the whole chat.');
    return {...base, kind: 'approval', title: 'Permission needed', choices: [...new Set(choices)] as ApprovalChoice[],
      command: optionalText(params.command, 'Command') ?? '', description: optionalText(params.description, 'Description') ?? ''};
  }
  if (method === 'sudo' || method === 'display.install.sudo') return {...base, kind: 'secret', title: 'Administrator password needed',
    prompt: 'Enter the administrator password for this computer.', detail: optionalText(params.command, 'Command'), code: false};
  if (method === 'secret') return {...base, kind: 'secret', title: 'Private value needed',
    prompt: text(params.prompt, 'Secret prompt'), detail: text(params.env_var, 'Secret name'), code: false};
  if (method === 'vault.unlock_prompt') {
    text(params.backend, 'Password manager');
    return {...base, kind: 'secret', title: 'Unlock your password manager',
      prompt: `Enter your password for ${text(params.display_name, 'Password manager name')}.`, detail: null, code: false};
  }
  if (method === 'vault.code') return {...base, kind: 'secret', title: 'Verification code needed',
    prompt: optionalText(params.site, 'Site') ? `Enter the verification code for ${params.site}.` : 'Enter the verification code.',
    detail: optionalText(params.hint, 'Code hint'), code: true};
  if (method === 'vault.save_login') return {...base, kind: 'login', title: 'Save a sign-in',
    site: text(params.site, 'Site'), origin: text(params.origin, 'Site address')};
  const known = desktopMethods.has(method);
  return {...base, kind: 'unsupported', title: known ? 'This app feature is not available' : 'This request type is not supported', known,
    reason: known ? `Hermes requested ${method}. EightBots does not provide this Hermes Desktop control.`
      : `Hermes requested ${method}. EightBots has no response form for this request type.`};
}

/** Validate against the current open request before sending any response to Hermes. */
export function validateNativeResponse(request: Record<string, unknown>, result: Record<string, unknown>): Record<string, unknown> {
  const parsed = parseNativeRequest(request);
  if (parsed.kind === 'unsupported') throw new Error(parsed.reason);
  if (parsed.kind === 'clarify') {
    only(result, ['answers']);
    if (result.answers === undefined || result.answers === null) return result;
    const answers = object(result.answers, 'Answers');
    const pending = parsed.questions.filter(question => !Object.hasOwn(parsed.locked, question.id));
    if (Object.keys(answers).some(id => !pending.some(question => question.id === id))) throw new Error('An answer refers to a question that is already answered or not in this request.');
    for (const question of pending) {
      if (!Object.hasOwn(answers, question.id) || (answers[question.id] !== null && typeof answers[question.id] !== 'string')) throw new Error('Answer or skip each question before sending.');
    }
  } else if (parsed.kind === 'setup') {
    only(result, ['picked', 'label', 'said']);
    const picked = result.picked ?? null, label = result.label ?? null;
    if (result.said !== undefined && result.said !== null && typeof result.said !== 'string') throw new Error('Your additional answer must be text.');
    if (picked === null) {
      if (label !== null) throw new Error('An option label needs a selected option.');
    } else {
      const ids = parsed.multi ? picked : [picked], labels = parsed.multi ? label : [label];
      if (!Array.isArray(ids) || !Array.isArray(labels) || ids.length !== labels.length || new Set(ids).size !== ids.length) throw new Error('The selected setup options are invalid.');
      ids.forEach((id, index) => {
        if (typeof id !== 'string' || !parsed.options.some(option => option.id === id && option.label === labels[index])) throw new Error('A selected option does not match this request.');
      });
    }
  } else if (parsed.kind === 'approval') {
    only(result, ['choice', 'all']);
    if (!parsed.choices.includes(result.choice as ApprovalChoice)) throw new Error('This permission choice is not available for this request.');
    if (result.all !== undefined && result.all !== null && result.all !== false) throw new Error('Respond to each permission request separately.');
  } else {
    only(result, ['value']);
    text(result.value, 'Private response', true);
    if (parsed.kind === 'login' && result.value !== '') {
      let login: Record<string, unknown>;
      try {login = object(JSON.parse(String(result.value)), 'Sign-in');} catch {throw new Error('The sign-in response must contain a username and password.');}
      only(login, ['identifier', 'password']);
      text(login.identifier, 'Username'); text(login.password, 'Password');
    }
  }
  return result;
}

export function nativeRequestCancellation(request: Record<string, unknown>, message: string):
  {result: Record<string, unknown>} | {error: {code: number; message: string}} {
  const error = {code: -32800, message: text(message, 'Cancellation message')};
  let parsed: NativeRequest;
  try {parsed = parseNativeRequest(request);} catch {return {error};}
  if (parsed.kind === 'clarify') return {result: {}};
  if (parsed.kind === 'setup') return {result: {picked: null, label: null, said: null}};
  if (parsed.kind === 'approval' && parsed.choices.includes('deny')) return {result: {choice: 'deny'}};
  if (parsed.kind === 'secret' || parsed.kind === 'login') return {result: {value: ''}};
  return {error};
}
